import { log } from "mathjs";
import { nanoid } from 'nanoid';
import { CREATE_PROJECT, CREATE_PROJECT_REVISION, DELETE_PROJECT, DELETE_PROJECTS, EXPORT_PROJECT, GET_DEMO_PROJECTS_LIST, GET_PROJECT_BY_ID, GET_PROJECT_EXPORT_JOB, GET_PROJECT_FOR_EXPORT, GET_PROJECT_FOR_EXPORT_REVISION, GET_PROJECT_SAVE_FILE_ID, IMPORT_PROJECT, LIST_PROJECT_REVISIONS, LIST_PROJECTS, LOAD_PROJECT_SAVE_FILES, PUBLISH_VERSION, SAVE_PROJECT, UPDATE_PROJECT, UPDATE_PROJECT_REVISION } from "./gql";
import { GQLSession } from '../GQLSession';
import { ApolloGQLClient, MutationBlob } from "../ApolloGQLClient";
import APIUtility from "../../../APIUtility";
import Scenario from "../../../scenarioElements/Scenario";
import { Asset, AssetTypeEnum, AuditLog, CreateProjectImportJobMutationInput, CreateProjectSaveFileMutationInput, DemoProject, InputMaybe, NodeResourcePermissionInputObject, PermissionPermissableTypeEnum, Project, ProjectExportJob, ProjectExportJobAssetPackagingEnum, ProjectImportJob, ProjectSaveFile, ProjectSaveFileSaveTypeEnum, ProjectStateEnum, ScenarioRelease, UpdateProjectMutationInput, UpdateScenarioMutationInput, User } from "../../../cms-glue/gql";
import { ProjectListParams, ScenarioMetadata } from "../../../../interfaces/API";
import { CmsProject } from "../../../cms-glue/types";
import GQLUser from '../user/GQLuser';
import { CREATE_FOLDER } from '../assets/gql';
import SessionUtils from "../../../utilities/SessionUtils";
import { FeatureFlagNames } from "../../../../interfaces/enums";
import NotificationPrompter from "../../../../components/prompts/NotificationPrompter";
import { ISaveServerResponse } from "../../../../interfaces/Responses";
import { assert, ErrorCategory } from "../../../utilities/ErrorHandling";
export default class GQLProjects 
{	
	static renameProject = async (projectName: string, projectId?: string) => 
	{
		const jwt = APIUtility.getAccessToken();
		if(!jwt) 
		{
			return false;
		}
		const id = projectId || Scenario.current()?.id;
		if (!id) 
		{
			return false;
		}
		const variables = {
			input: {
				databaseId: id,
				name: projectName
			}
		};
			
		const data = await ApolloGQLClient.mutate<{ databaseId: string; name: string }>(UPDATE_PROJECT, variables, 'request to rename project');

		const project: Project = data.updateProject.project;

		console.info({
			projectId: project.databaseId,
			name: projectName,
			jwt,
			data
		}, 'response to rename a project');

		if (data.updateProject.errors.length > 0) 
		{
			throw new Error(data.updateProject.errors[0].fullMessage);
		}
	
		return true;
	}

	static async updateProjectState(projectId: string, newState: string)
	{
		const variables = {
			input: {
				databaseId: projectId,
				state: newState,
			}
		};

		const data = await ApolloGQLClient.mutate<{ databaseId: string; state: string }>(UPDATE_PROJECT, variables, 'request to update a project state');
		const project: Project = data.updateProject.project;

		console.info({
			id: project.databaseId,
			state: newState,
			data: data
		}, 'response to update a project');

		return project;
	}

	static async updateProjectWhitelist(externalUserIds: string[], projectId: string)
	{

		console.info({projectId: projectId, externalUsers: externalUserIds },
			'request to update whitelisted authors for a project');
	
		
		const cmsUsers = await GQLUser.getUsersByIds(externalUserIds);
	
		if (cmsUsers.length === 0) 
		{
			return;
		}
	
		const project = await this.shareProjectWithUsers(projectId, cmsUsers);
	
		if (project) 
		{
			return {
				'status': 'project whitelist updated successfully'
			};
		}
		else 
		{
			throw new Error("Project not updated");
		}
	}
	
	static async shareProjectWithUsers(projectId: string, users: User[])
	{
		const resourcePermissions = users.map((user) => 
		{
			const permission = GQLUser.gqlUserToPermission(user);
			if (permission instanceof Error) 
			{
				throw permission;
			}
			return {
				permissableId: user.id,
				permissableType: PermissionPermissableTypeEnum.User,
				permission
			};
		});

		const variables = {
			input: {
				databaseId: projectId,
				replaceResourcePermissions: resourcePermissions
			}
		};
		
		const data = await ApolloGQLClient.mutate<{ 
			databaseId: string;
			replaceResourcePermissions: InputMaybe<NodeResourcePermissionInputObject>[] }
		>(UPDATE_PROJECT, variables, 'request to update a project state');

		const project: Project = data.updateProject.project;

		console.info({
			id: project.databaseId,
			resourcePermissions: resourcePermissions,
			data: data
		}, 'response to update a project');

		return project;
	}

	static async updateProjectResourcePermissions(jwt: string,
		projectId: string, resourcePermissions: UpdateProjectMutationInput["replaceResourcePermissions"])
	{
		const variables = {
			input: {
				databaseId: projectId,
				replaceResourcePermissions: resourcePermissions || []
			}
		};

		const data = await ApolloGQLClient.mutate<{ 
			databaseId: string; 
			replaceResourcePermissions: InputMaybe<NodeResourcePermissionInputObject[]>
		}>(UPDATE_PROJECT, variables, 'request to update a project resource permissions');

		const project: Project = data.updateProject.project;

		console.info({
			id: project.databaseId,
			resourcePermissions,
			jwt: jwt,
			data: data
		}, 'response to update a project resource permissions');

		return project;
	}

	private static mapSavefile(projectId: string, savefile: ProjectSaveFile) 
	{
		assert(savefile && projectId, 'savefile and projectId are required', ErrorCategory.ProjectLoad);
		return {
			url: savefile.file!.fileUrl!,
			revisionId: savefile.id!,
			creationDate: savefile.createdAt!,
			createVersion: savefile.scopeCreateVersion!,
			extensionsVersion: savefile.extensionsVersion!,
			project: projectId!,
			email: savefile.createdBy?.email || '',
			username: savefile.createdBy?.username || ''
		};
	}

	static async loadProjectWithSaveFiles(projectId: string, lastSaveFiles = 1): Promise<ISaveServerResponse>
	{
		const variables = {
			databaseId: projectId,
			lastSaveFiles
		};

		const data = await ApolloGQLClient.query(LOAD_PROJECT_SAVE_FILES, variables, 'request to load project save files');

		
		console.info({
			data
		}, 'response to load project save files');
		
		const project = data.project;
		type MappedSaveFile = ReturnType<typeof GQLProjects.mapSavefile>;
		const auto: MappedSaveFile[] = [];
		const manual: MappedSaveFile[] = [];

		if (project.autoSavefiles.nodes) 
		{
			for (const savefile of project.autoSavefiles.nodes as ProjectSaveFile[]) 
			{
				if (savefile.file) 
				{
					auto.push(GQLProjects.mapSavefile(projectId, savefile));
				}
			}
		}

		if (project.manualSavefiles.nodes) 
		{
			for (const savefile of project.manualSavefiles.nodes as ProjectSaveFile[]) 
			{
				if (savefile.file) 
				{
					manual.push(GQLProjects.mapSavefile(projectId, savefile));
				}
			}
		}

		return {saves: {
			auto,
			manual
		}};
	}

	static async saveProject(projectDatabaseId: string, scopeCreateVersion: string,
		extensionsVersion: string, assets: string[], name: string, duplicate: boolean, saveType: string, blob: Blob, fileName: string)
	{
		const variables = {
			input: {
				projectDatabaseId,
				scopeCreateVersion,
				extensionsVersion,
				assetDatabaseIds: assets?.filter(id => !!id),
				name,
				duplicate,
				saveType: saveType === "manual" ? ProjectSaveFileSaveTypeEnum.Manual : ProjectSaveFileSaveTypeEnum.Auto,
				file: {
					fileName,
					blob
				} as MutationBlob
			} as CreateProjectSaveFileMutationInput
		};

		const data = await ApolloGQLClient.mutate(SAVE_PROJECT, variables, 'request to save project');

		const saveFile: ProjectSaveFile = data.createProjectSaveFile.projectSaveFile;

		console.info({
			saveFileId: saveFile?.databaseId,
			projectId: saveFile?.project?.databaseId,
			data
		}, 'response to save a project');

		return saveFile;
	}

	static async getProjectSaveFileId(databaseId: string)
	{
		const variables = {
			databaseId
		}

		const data = await ApolloGQLClient.query(GET_PROJECT_SAVE_FILE_ID, variables, 'request to get project save file id');

		return data.project.currentSaveFile?.id;
	}

	static async getProjectForExport(jwt: string, databaseId: string)
	{
		const variables = {
			databaseId
		}

		const data = await ApolloGQLClient.query(GET_PROJECT_FOR_EXPORT, variables, 'request to get project for export');

		console.info({
			jwt,
			data
		}, 'response to get project for export request');

		return data.project
	}

	static async getProjectForExportRevision(jwt: string, databaseId: string, projectSaveFileDatabaseId: string)
	{
		const variables = {
			databaseId,
			projectSaveFileDatabaseId
		}

		const data = await ApolloGQLClient.query(GET_PROJECT_FOR_EXPORT_REVISION, variables, 'request to get project for export revision');

		console.info({
			jwt,
			data,
		}, 'response to get project for export revision request');

		return data.project
	}

	static async getProjectExportJob(jwt: string, projectDatabaseId: string, databaseId: string)
	{
		const variables = {
			projectDatabaseId,
			databaseId
		}

		const data = await ApolloGQLClient.query(GET_PROJECT_EXPORT_JOB, variables, 'request to get project export job');
		
		console.info({
			jwt,
			data
		}, 'response to get project export job request');

		return data.projectExportJobs.nodes[0];
	}
	
	static get AssetPackagingsMap(): { [index: string]: ProjectExportJobAssetPackagingEnum }
	{
		return {
			"deep": ProjectExportJobAssetPackagingEnum.Deep,
			"processed only": ProjectExportJobAssetPackagingEnum.ProcessedOnly,
			"shallow": ProjectExportJobAssetPackagingEnum.Shallow
		}
	}

	static get AssetPackagingsMapInverse(): { [index: string]: string }
	{
		return {
			[ProjectExportJobAssetPackagingEnum.Deep]: "deep",
			[ProjectExportJobAssetPackagingEnum.ProcessedOnly]: "processed only",
			[ProjectExportJobAssetPackagingEnum.Shallow]: "shallow"
		}
	}

	static async exportProject(jwt: string, projectDatabaseId: string, projectSaveFileDatabaseId: string, assetPackaging: string)
	{
		const variables = {
			input: {
				projectDatabaseId,
				projectSaveFileDatabaseId,
				assetPackaging: this.AssetPackagingsMap[assetPackaging]
			}
		};

		const data = await ApolloGQLClient.mutate<{ 
			projectDatabaseId: string,
			projectSaveFileDatabaseId: string,
			assetPackaging: ProjectExportJobAssetPackagingEnum}>(EXPORT_PROJECT, variables, 'request to export a project');
		
		const projectExportJob: ProjectExportJob = data.createProjectExportJob.projectExportJob;

		console.info({
			jwt,
			data
		}, 'response to export a project');

		return projectExportJob;
	}

	static async createFolder(jwt: string, name: string, folderId: string)
	{
		const variables = {
			input: {
				name,
				folderId,
				type: AssetTypeEnum.Folder
			}
		};

		const data = await ApolloGQLClient.mutate<{ 
			name: string,
			folderId: string,
			type: AssetTypeEnum}>(CREATE_FOLDER, variables, 'request to create a folder');

		const folder: Asset = data.createAsset.asset;
		const errors = data.createAsset.errors;

		console.info({
			jwt,
			data
		}, 'response to create a folder');

		return { folder, errors };
	}

	private static gqlUserToCmsProjectPublishInfo(currentRelease: ScenarioRelease): CmsProject["publish_info"]
	{
		return {
			sdk_version: currentRelease?.sdkVersion,
			session_type: currentRelease?.type ? GQLSession.SessionTypesMapInverse[currentRelease?.type] : undefined,
			user_info: {
				id: currentRelease?.author?.databaseId,
				name: currentRelease?.author?.name,
				email: currentRelease?.author?.email,
				username: currentRelease?.author?.username,
				permalink: currentRelease?.author?.permalink
			},
			published_at: currentRelease?.publishedAt || new Date().toISOString()
		};
	}

	private static gqlProjectToCmsProject(project: Project) : CmsProject
	{
		let auditLogs: Partial<AuditLog>[] | undefined = undefined;

		if (project?.auditLogs?.nodes && project?.auditLogs?.nodes?.length > 0) 
		{
			auditLogs = project.auditLogs.nodes.map((log) => 
			{
				return {
					userId: log?.user?.databaseId,
					userName: log?.userName,
					data: log?.data,
					date: log?.date
				}
			});
		}

		const projectName = SessionUtils.isFeatureFlagEnabled(FeatureFlagNames.RevisionManagement) ? project.revisionGroupName : project.name;

		return {
			_id: project.databaseId as string,
			creation_date: project.createdAt || '',
			external_creator_id: project.createdBy?.databaseId || '',
			name:  projectName || '',
			owner: project.createdBy?.id || '',
			state: project.state || ProjectStateEnum.WorkInProgress,
			updated_at:  project.updatedAt || '',
			revisionName: project.revisionName || '',
			revisionNumber: project.revisionNumber || undefined,
			revisionNotes: project.revisionNotes || undefined,
			scope_create_version: project.scopeCreateVersion || '',
			updated_by_external_id: project.updatedBy?.databaseId || '',
			originalAuthor: project.createdBy?.databaseId || '',
			originalAuthorName: project.createdBy?.name || '',
			lastAuthor: project.updatedBy?.databaseId || '',
			lastAuthorName: project.updatedBy?.name || '',
			lastSaveDate: project.updatedAt || '',
			publish_info: this.gqlUserToCmsProjectPublishInfo(project.scenarios?.[0]?.currentRelease as ScenarioRelease),
			auditLogs: auditLogs || [],
			scenarios: project.scenarios || [],
			resourcePermissions: project.resourcePermissions,
			permissions: project.permissions,
			revisions: project.revisions?.map(rev => this.gqlProjectToCmsProject(rev as Project))
		};
	}

	static async listProjects(variables: Partial<ProjectListParams> = {})
	{
		const data = await ApolloGQLClient.query(LIST_PROJECTS, variables, 'listProjects');
		const projects: Project[] = data.projects.nodes;
		return {
			projects: projects.map((project) => this.gqlProjectToCmsProject(project)),
			totalCount: data.projects.totalCount,
			creators: data.projects.creators,
			permissables: data.projects.permissables
		};
	}

	static async getDemoProjectsList(jwt: string)
	{
		const variables = {};

		const data = await ApolloGQLClient.query(GET_DEMO_PROJECTS_LIST, variables, 'request to get the demo projoects list');

		console.info({
			jwt,
			data
		}, 'response to getting the demo projects list');

		const demoProjects = data.demoProjects?.nodes || [];
		return demoProjects.map((demo: DemoProject) =>  ({
			projectKey: demo.name,
			imageFile: demo.previewFile?.fileUrl,
			jsonFile: demo.jsonFile?.fileUrl
		}));
	}

	static async importDemo(projectDatabaseId: string, demoProjectFileName: string)
	{
		const variables = {
			input: {
				projectDatabaseId,
				demoProjectFileName
			} as CreateProjectImportJobMutationInput
		};

		const data = await ApolloGQLClient.mutate<CreateProjectImportJobMutationInput>(IMPORT_PROJECT, variables, 'request to create a demo project import job');

		const job: ProjectImportJob = data.createProjectImportJob.projectImportJob;
		const errors = data.createProjectImportJob.errors;

		console.info({
			jobId: job?.databaseId,
			data
		}, 'response to create a demo project import job');

		return { job, errors };
	}

	static async getProjectByID(args = {})
	{
		const variables = {
			...args
		}

		const data = await ApolloGQLClient.query(GET_PROJECT_BY_ID, variables, 'request to get project by Id or databaseId');

		console.info({
			data
		}, 'response to get project request');

		const project: Project = data.project;
		return project;
	}

	static async getCmsProjectByID(args = {})
	{
		const project = await this.getProjectByID(args);
		return this.gqlProjectToCmsProject(project);
	}

	static async deleteProject(args = {})
	{
		const variables = {
			...args
		}

		const data = await ApolloGQLClient.mutate<{ databaseId: string, completeFileUpload: boolean}>(DELETE_PROJECT, variables, 'request to delete project');

		console.info({
			data
		}, 'response to delete project request');

		if (data?.deleteProject?.success) 
		{
			return true;
		}
		else 
		{
			throw new Error(data?.deleteProject?.errors?.fullMessage);
		}
	}

	static async deleteProjects(args = {})
	{
		const variables = {
			input: {
				...args
			}
		}

		const data = await ApolloGQLClient.mutate<{ databaseIds: string[]}>(DELETE_PROJECTS, variables, 'request to delete projects');

		console.info({
			data
		}, 'response to delete projects request');

		if (data?.deleteProjects?.success) 
		{
			return true;
		}
		else 
		{
			throw new Error(data?.deleteProjects?.errors?.fullMessage);
		}
	}

	static async createProject(name: string, metadata?: ScenarioMetadata)
	{
		if (!name.trim()) name = "Untitled Project";
		const variables = {
			input: {
				name,
				revisionName: metadata?.revisionName ?? "v1",
				revisionNotes: metadata?.revisionNotes || 'WL Create 2.0 Scenario'
			}
		};

		let data = await ApolloGQLClient.mutate<{name: string}>(CREATE_PROJECT, variables, 'request to create a project');

		let project: Project = data.createProject.project;

		if(!project)
		{
			if(data.createProject.errors[0].fullMessage.startsWith("Name has already been taken")) 
			{
				// generate a new name
				variables.input.name += " " + nanoid();

				data = await ApolloGQLClient.mutate<{name: string}>(CREATE_PROJECT, variables, 'Create project');
				project = data.createProject.project;

				if(!project)
				{
					throw new Error(data.createProject.errors?.[0]?.message);
				}
			}
			else 
			{
				throw new Error(data?.createProject.errors?.[0]?.message);
			}
		}

		console.info({
			name,
			data
		}, 'response to create a project');

		return this.gqlProjectToCmsProject(project);
	}

	static async publishVersion(scenarioId: string, blob: Blob, fileName: string, externalData: any
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
		, recipientGroups: string[] = [], sdkVersion = '2.0.0', sessionType = 0,
		resourcePermissions: UpdateScenarioMutationInput["replaceResourcePermissions"]
	)
	{
		const variables = {
			input: {
				databaseId: parseInt(scenarioId),
				scopeFile: {
					fileName: fileName,
					blob
				} as MutationBlob,
				requestedGroups: recipientGroups,
				replaceResourcePermissions: resourcePermissions,
				scenarioRelease: {
					sdkVersion: sdkVersion,
					sceneName: 'obsolete',
					// serializationVersion: serializationVersion, // Not currently supported by the CMS
					sessionType: GQLSession.sessionTypeToGql(sessionType),
					externalData: externalData
				}
			}
		};
	
		const data = await ApolloGQLClient.mutate(PUBLISH_VERSION, variables, 'request to publish a version');
	
		const scenario: Scenario = data.updateScenario.scenario;
	
		console.info({
			scenarioId: scenario.id,
			'groups': recipientGroups,
			data
		}, 'response to publish a version');
	
		return scenario;
	}

	static async listProjectRevisions(projectId: string)
	{
		const variables = {
			projectId
		};

		const data = await ApolloGQLClient.query(LIST_PROJECT_REVISIONS, variables, 'request to list project revisions');

		if (data.errors?.length > 0)
		{
			throw new Error(data.errors[0].fullMessage);
		}

		return data.project;
	}

	static async updateProjectRevision(projectId: string, newDetails:{revisionName: string, revisionNotes: string})
	{
		const variables = {
			input: {
				databaseId: projectId,
				revisionName: newDetails.revisionName,
				revisionNotes: newDetails.revisionNotes
			}
		};

		const data = await ApolloGQLClient.mutate(UPDATE_PROJECT_REVISION, variables, 'request to update a project revision');

		return data.updateProject.project;
	}

	static async createProjectRevision(sourceProjectSaveFileId: string, newDetails:{revisionName: string, revisionNotes: string}) 
	{
		const variables = {
			input: {
				revisionSourceProjectSaveFileId: sourceProjectSaveFileId,
				revisionName: newDetails.revisionName,
				revisionNotes: newDetails.revisionNotes
			}
		};

		const data = await ApolloGQLClient.mutate(CREATE_PROJECT_REVISION, variables, 'request to create a project revision');

		return data.createProject.project;
	}
}
